import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useProductDetailContext } from "../context/ProductDetailContext";

import SubProductForm from "./SubProductForm";

import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useQueryID from "@/hooks/useQueryId";
import {
  useGetSingleSubProductQuery,
  useMutateSubProductsMutation,
} from "@/redux/api/toolsQuery";
import {
  TUpdateSubProduct,
  defaultSubProduct,
  updateSubProductSchema,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import {
  filterEmptyValues,
  parseToFormData,
  validateResponse,
} from "@/utils/helper-functions/form.helper";

interface UpdateSubProductProps {
  isOpen: boolean;
  toggleUpdateSubProductModal: () => void;
}

const UpdateSubProductModal = ({
  isOpen,
  toggleUpdateSubProductModal,
}: UpdateSubProductProps): JSX.Element => {
  const { productId } = useProductDetailContext();
  const { id: subProductId, reset } = useQueryID(ToolsKeys.SUB_PRODUCT_ID);
  const [updateSubProduct, { isLoading }] = useMutateSubProductsMutation();
  const { data, isLoading: isSubProductLoading } = useGetSingleSubProductQuery({
    id: productId,
    subProductId,
  });

  const singleSubProduct = data?.data;

  const form = useForm<TUpdateSubProduct>({
    resolver: zodResolver(updateSubProductSchema),
    ...(singleSubProduct && {
      values: {
        id: singleSubProduct.id,
        files: singleSubProduct.files ?? [],
        images: singleSubProduct.images ?? [],
        name: singleSubProduct.name ?? "",
        modelNumber: singleSubProduct?.modelNumber ?? "",
      },
    }),
    defaultValues: defaultSubProduct,
  });

  const handleExit = (): void => {
    form.reset();
    reset();
    toggleUpdateSubProductModal();
  };

  const onSubmit = async (data: TUpdateSubProduct): Promise<void> => {
    const existingImages =
      Array.isArray(data?.images) &&
      data?.images?.filter(
        (image) => typeof image === "string" && image.startsWith("http"),
      );

    const existingFiles =
      Array.isArray(data?.files) &&
      data?.files?.filter(
        (file) => typeof file === "string" && file.startsWith("http"),
      );

    const images = data.images?.filter((image) => typeof image !== "string");
    if (!images || (Array.isArray(images) && images.length === 0)) {
      delete data.images;
    }
    const files = data.files?.filter((file) => typeof file !== "string");
    if (!files || (Array.isArray(files) && files.length === 0)) {
      delete data.files;
    }

    const dataToSent = parseToFormData(
      filterEmptyValues(data),
      ["files", "images"],
      form.formState.dirtyFields,
    );

    if (Array.isArray(existingImages) && existingImages?.length > 0) {
      dataToSent.append("existingImages", JSON.stringify(existingImages));
    }
    if (Array.isArray(existingFiles) && existingFiles?.length > 0) {
      dataToSent.append("existingFiles", JSON.stringify(existingFiles));
    }

    const response = await updateSubProduct({
      method: "PUT",
      productId,
      subProductId,
      data: dataToSent,
    });

    validateResponse({
      response,
      onSuccess: () => {
        handleExit();
        customToast.success(
          ToolsConstants.FORM.SUB_PRODUCT.UPDATED_SUB_PRODUCT_SUCCESS,
        );
      },
    });
  };

  return (
    <FormDialog
      isOpen={isOpen}
      title={ToolsConstants.FORM.SUB_PRODUCT.UPDATE_SUB_PRODUCT}
      toggleModal={handleExit}
      isFormDirty={form.formState.isDirty}
      isLoading={isSubProductLoading}
      className=' px-3'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
          <SubProductForm />
          <div className=' flex justify-between'>
            <Button
              variant={"ghost"}
              onClick={() => form.reset()}
              disabled={isLoading}
            >
              {Button_Names.Discard_Change}
            </Button>
            <Button
              type='submit'
              className='w-[200px]'
              disabled={!form.formState.isDirty}
              isLoading={isLoading}
            >
              {Button_Names.Save_Changes}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default UpdateSubProductModal;
